import { Injectable } from '@angular/core'
import { AlarmAppInstallModalComponent } from 'src/app/common/modals/alarm-app-install-modal/alarm-app-install-modal.component'
import { LogEvent } from 'src/app/interfaces/log'
import { PushCode, PushData } from 'src/app/interfaces/notification'
import { LogService } from '../log/log.service'
import { ModalService } from '../modal/modal.service'
import { RouterService } from '../router/router.service'

interface MoveToData {
  moveTo: string | undefined
}

@Injectable({
  providedIn: 'root',
})
export class PushNavigationService {
  constructor(
    private routerService: RouterService,
    private logService: LogService,
    private modalService: ModalService
  ) {}

  /** navigation 처리하기 전에 최종적으로 push data 셋팅 완료 */
  async getNavigationData(pushData: PushData): Promise<PushData | undefined> {
    if (pushData === undefined || pushData.code === undefined) {
      return
    }
    const moveToData: MoveToData = await this.getMoveTo(pushData)
    return {
      ...pushData,
      ...moveToData,
    }
  }

  /** 최종적으로 push 알림의 navigation을 처리 */
  async handlePushNotification(pushData: PushData) {
    const navigationData: PushData | undefined = await this.getNavigationData(pushData)
    if (navigationData?.moveTo) {
      this.navigateTo(navigationData)
    }
  }

  notificationClickLog(pushData: PushData): void {
    if (pushData !== undefined && pushData.moveTo !== undefined && !pushData.moveTo) {
      return
    }
    switch (pushData.code) {
      case PushCode.MBTI_GUEST_QUIZ_CREATED:
        this.logService.eventLogging(LogEvent.NOTIFICATION_CLICK_NEW_QUIZ_RESPONSE)
        return
      case PushCode.MBTI_FRIEND_HOST_QUIZ_CREATED:
        this.logService.eventLogging(LogEvent.NOTIFICATION_CLICK_NEW_QUIZ)
        return
      case PushCode.FAVOUR_TOURNAMENT_FRIEND_HOST_QUIZ_CREATED:
        this.logService.eventLogging(LogEvent.NOTIFICATION_CLICK_NEW_FW)
        return
      case PushCode.FAVOUR_TOURNAMENT_GUEST_QUIZ_CREATED:
        this.logService.eventLogging(LogEvent.NOTIFICATION_CLICK_NEW_FW_RESPONSE)
        return
      default:
        this.logService.eventLogging(LogEvent.NOTIFICATION_CLICK_ETC)
    }
  }

  /** push알림의 실질적인 navigation 로직 실행 */
  private navigateTo(navigationData: PushData): void {
    if (navigationData !== undefined && navigationData !== null) {
      // 혹시나 같은 페이지에서 navigateTo가 실행되면 return
      const moveTo: string | undefined = navigationData.moveTo
      if (moveTo !== undefined) {
        if (!this.routerService.isSameAsCurrentUrl(moveTo)) {
          const queryParamString = moveTo.split('?')[1]
          const queryParams: Record<string, string> = {}
          if (queryParamString !== undefined) {
            queryParamString.split('&').forEach((param) => {
              const splittedParam = param.split('=')
              if (splittedParam.length === 2) {
                queryParams[splittedParam[0]] = splittedParam[1]
              }
            })
          }
          this.routerService.navigate(moveTo.split('?')[0], { queryParams })
        } else {
          return
        }
      } else {
        this.routerService.navigate('gnb/main')
      }
    }
  }

  /**
   * pushCode에 맞게 navigate할 페이지 url을 moveTo에 setting
   * 예외가 아닌 이상 moveTo는 ''으로 데이터가 옴
   * 예외인 경우에는 먼저 처리해주고 return
   */
  private async getMoveTo(pushData: PushData): Promise<MoveToData> {
    if (pushData !== undefined && pushData.moveTo !== undefined && pushData.moveTo !== null && pushData.moveTo !== '') {
      return { moveTo: pushData.moveTo }
    }
    switch (pushData.code) {
      case PushCode.MBTI_GUEST_QUIZ_CREATED:
        return { moveTo: `answer/${pushData.targetId}/host` }
      case PushCode.MBTI_FRIEND_HOST_QUIZ_CREATED:
        return { moveTo: `quiz/${pushData.targetId}` }
      case PushCode.FAVOUR_TOURNAMENT_FRIEND_HOST_QUIZ_CREATED:
        return { moveTo: `fw/${pushData.targetId}` }
      case PushCode.FAVOUR_TOURNAMENT_GUEST_QUIZ_CREATED:
        return { moveTo: `fw/detail/${pushData.targetId}/host` }
      case PushCode.FEATURE_FAVOUR_TOURNAMENT:
        return { moveTo: 'fw/main' }
      case PushCode.ASK_COMMENTED:
        return { moveTo: pushData.targetId ? `ask?targetCommentId=${pushData.targetId}` : '/' }
      case PushCode.MY_MBTI_TYPE_ASK_CREATED:
        return { moveTo: pushData.targetId ? `ask?targetPostId=${pushData.targetId}` : '/' }
      case PushCode.SURVEY_CREATED:
        return { moveTo: 'gnb/main/?scrollToSurvey=true' }
      case PushCode.MBTI_IDEAL_TYPE_FRIEND_QUIZ_CREATED:
        return { moveTo: pushData.targetId ? `ideal/${pushData.targetId}` : '/' }
      case PushCode.MBTI_IDEAL_TYPE_GUEST_MATCH_CREATED:
        return { moveTo: pushData.targetId ? `ideal/match/${pushData.targetId}` : '/' }
      case PushCode.MBTI_BEST_FRIEND_FRIEND_QUIZ_CREATED:
        return { moveTo: pushData.targetId ? `bf/${pushData.targetId}` : '/' }
      case PushCode.MBTI_BEST_FRIEND_GUEST_MATCH_CREATED:
        return { moveTo: pushData.targetId ? `bf/match/${pushData.targetId}` : '/' }
      case PushCode.SIGNAL_CONNECTED:
      case PushCode.SIGNAL_RECEIVED:
      case PushCode.SIGNAL_REPLIED:
        const signalDialog = await this.modalService.openDialog(
          AlarmAppInstallModalComponent,
          { appLink: 'signal' },
          'custom-center-round-modal'
        )
        await signalDialog.present()
        return { moveTo: undefined }
      case PushCode.CABINET_LETTER_RECEIVED:
        const CabinetDialog = await this.modalService.openDialog(
          AlarmAppInstallModalComponent,
          { appLink: 'cabinet' },
          'custom-center-round-modal'
        )
        await CabinetDialog.present()
        return { moveTo: undefined }
      case PushCode.LOG_POST_CHOSEN:
        const logPostChosenDialog = await this.modalService.openDialog(
          AlarmAppInstallModalComponent,
          { appLink: 'log/my-chosen-log-list' },
          'custom-center-round-modal'
        )
        await logPostChosenDialog.present()
        return { moveTo: undefined }
      case PushCode.LOG_POST_CORRECT:
      case PushCode.LOG_POST_INCORRECT:
        const logMyLogListDialog = await this.modalService.openDialog(
          AlarmAppInstallModalComponent,
          { appLink: 'log/my-log-list' },
          'custom-center-round-modal'
        )
        await logMyLogListDialog.present()
        return { moveTo: undefined }
      case PushCode.FRIEND_ACCEPT:
      case PushCode.FRIEND_REQUEST:
        const FriendsDialog = await this.modalService.openDialog(
          AlarmAppInstallModalComponent,
          { appLink: 'friends' },
          'custom-center-round-modal'
        )
        await FriendsDialog.present()
        return { moveTo: undefined }
      case PushCode.GROUP_CHEMI_STARTED:
        return { moveTo: pushData.targetId ? `group-chemi/${pushData.targetId}` : '/' }
      case PushCode.PROMOTION:
        return { moveTo: pushData.moveTo ?? '/' }
      default:
        return { moveTo: '/' }
    }
  }
}
